export type WebGameSoundUiLang = 'en' | 'ro';

export type WebGameSoundMomentBinding = 'backgroundMusic' | 'sfxCatalog';

export interface WebGameSoundMomentDefinition {
    key: string;
    labels: Record<WebGameSoundUiLang, string>;
    typicalDurationSeconds: number | null;
    description: Record<WebGameSoundUiLang, string>;
    defaultPath?: string;
    binding?: WebGameSoundMomentBinding;
    configurableInDashboard: boolean;
}

export interface WebGameSoundGameProfile {
    gameCode: string;
    templateName: string;
    supportsMetadataSounds: boolean;
    supportsBackgroundMusic: boolean;
    notes: Record<WebGameSoundUiLang, string>;
    moments: WebGameSoundMomentDefinition[];
}

const LOOP_DURATION_SECONDS: number | null = null;

const SHARED_WIN_LOSS_MOMENTS: WebGameSoundMomentDefinition[] = [
    {
        key: 'win',
        labels: { en: 'Win result', ro: 'Câștig' },
        typicalDurationSeconds: 0.73,
        description: {
            en: 'Short celebration sting when the round pays out.',
            ro: 'Sunet scurt de celebrare când runda plătește.'
        },
        defaultPath: '/webgames/sounds/bet-won.mp3',
        binding: 'sfxCatalog',
        configurableInDashboard: true
    },
    {
        key: 'loss',
        labels: { en: 'Loss result', ro: 'Pierdere' },
        typicalDurationSeconds: 1.1,
        description: {
            en: 'Sad / try-again sting when the wager is lost.',
            ro: 'Sunet de pierdere / încearcă din nou când pariul e pierdut.'
        },
        defaultPath: '/webgames/sounds/bet-lost.mp3',
        binding: 'sfxCatalog',
        configurableInDashboard: true
    }
];

export const WEB_GAME_SOUND_GAME_PROFILES: WebGameSoundGameProfile[] = [
    {
        gameCode: 'dice',
        templateName: 'dice.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: true,
        notes: {
            en: 'Dice reads metadata.backgroundMusic and metadata.sfxCatalog from GET /config.',
            ro: 'Dice citește metadata.backgroundMusic și metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'background',
                labels: { en: 'Background loop', ro: 'Fundal (buclă)' },
                typicalDurationSeconds: LOOP_DURATION_SECONDS,
                description: {
                    en: 'Optional looped ambience; leave unset to disable background music.',
                    ro: 'Ambianță opțională în buclă; lasă neales pentru a dezactiva muzica de fundal.'
                },
                binding: 'backgroundMusic',
                configurableInDashboard: true
            },
            {
                key: 'roll',
                labels: { en: 'Dice roll', ro: 'Aruncare zaruri' },
                typicalDurationSeconds: 1.32,
                description: {
                    en: 'Thrown while dice animate; template may stagger two clips (~55 ms apart).',
                    ro: 'La animarea zarurilor; template-ul poate decală două clipuri (~55 ms).'
                },
                defaultPath: '/webgames/assets/dice/dice-roll.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    },
    {
        gameCode: 'slot-machine',
        templateName: 'slot-machine.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: false,
        notes: {
            en: 'Slot machine reads metadata.backgroundMusic and metadata.sfxCatalog from GET /config.',
            ro: 'Slot machine citește metadata.backgroundMusic și metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'spin',
                labels: { en: 'Reel spin loop', ro: 'Rotire role (buclă)' },
                typicalDurationSeconds: 12,
                description: {
                    en: 'Looped wheel sound while reels spin.',
                    ro: 'Sunet de roată în buclă cât timp rolele se rotesc.'
                },
                defaultPath: '/webgames/sounds/slots-wheel.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            {
                key: 'reelStop',
                labels: { en: 'Reel stop', ro: 'Oprire rolă' },
                typicalDurationSeconds: 1.32,
                description: {
                    en: 'Plays as each reel settles.',
                    ro: 'La oprirea fiecărei role.'
                },
                defaultPath: '/webgames/sounds/reel_stop.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            {
                key: 'win',
                labels: { en: 'Win celebration', ro: 'Câștig' },
                typicalDurationSeconds: 5.38,
                description: {
                    en: 'Money win sting during the 4 s celebration overlay.',
                    ro: 'Sunet de câștig în timpul overlay-ului de celebrare (4 s).'
                },
                defaultPath: '/webgames/sounds/slots-money-win.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            {
                key: 'loss',
                labels: { en: 'Loss result', ro: 'Pierdere' },
                typicalDurationSeconds: 1.1,
                description: {
                    en: 'Shared lost sting after losing reels settle.',
                    ro: 'Sunet comun de pierdere după oprirea rolelor.'
                },
                defaultPath: '/webgames/sounds/bet-lost.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            }
        ]
    },
    {
        gameCode: 'lucky-cells',
        templateName: 'lucky-cells.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: false,
        notes: {
            en: 'Lucky Cells reads metadata.sfxCatalog from GET /config.',
            ro: 'Lucky Cells citește metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'flip',
                labels: { en: 'Cell flip', ro: 'Întoarcere celulă' },
                typicalDurationSeconds: 0.53,
                description: {
                    en: 'Plays when a cell is revealed.',
                    ro: 'La dezvăluirea unei celule.'
                },
                defaultPath: '/webgames/sounds/flip_sound.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    },
    {
        gameCode: 'higher-lower',
        templateName: 'higher-lower.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: true,
        notes: {
            en: 'Higher/Lower reads metadata.backgroundMusic and metadata.sfxCatalog from GET /config.',
            ro: 'Higher/Lower citește metadata.backgroundMusic și metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'backgroundMusic',
                labels: { en: 'Background loop', ro: 'Fundal (loop)' },
                typicalDurationSeconds: LOOP_DURATION_SECONDS,
                description: {
                    en: 'Optional ambience loop while the game is open.',
                    ro: 'Loop opțional de fundal cât timp jocul este deschis.'
                },
                binding: 'backgroundMusic',
                configurableInDashboard: true
            },
            {
                key: 'clip',
                labels: { en: 'Card clip', ro: 'Clip carte' },
                typicalDurationSeconds: 1.32,
                description: {
                    en: 'Reel / envelope cue when a reference or draw digit lands (trimmed to reel timing).',
                    ro: 'Sunet de rolă / plic când se oprește cifra de referință sau extrasă (tăiat la durata rolei).'
                },
                defaultPath: '/webgames/sounds/reel_stop.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            {
                key: 'uiTap',
                labels: { en: 'UI tap', ro: 'Tap UI' },
                typicalDurationSeconds: 0.32,
                description: {
                    en: 'Short tap feedback on wager controls and bet-type choices.',
                    ro: 'Feedback scurt la pariu și la alegerea mai mare / mai mic.'
                },
                defaultPath: '/webgames/sounds/reel_stop.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    },
    {
        gameCode: 'head-tails-coin',
        templateName: 'head-tails-coin.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: false,
        notes: {
            en: 'Head/Tails reads metadata.sfxCatalog from GET /config.',
            ro: 'Head/Tails citește metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'settle',
                labels: { en: 'Coin settle', ro: 'Oprire monedă' },
                typicalDurationSeconds: 1.32,
                description: {
                    en: 'Plays when the coin lands.',
                    ro: 'Când moneda se oprește.'
                },
                defaultPath: '/webgames/sounds/reel_stop.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    },
    {
        gameCode: 'war',
        templateName: 'war.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: false,
        notes: {
            en: 'War reads metadata.sfxCatalog from GET /config.',
            ro: 'War citește metadata.sfxCatalog din GET /config.'
        },
        moments: SHARED_WIN_LOSS_MOMENTS
    },
    {
        gameCode: 'wheel-of-fortune',
        templateName: 'wheel-of-fortune.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: false,
        notes: {
            en: 'Wheel Of Fortune reads metadata.sfxCatalog from GET /config.',
            ro: 'Wheel Of Fortune citește metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'spinTick',
                labels: { en: 'Wheel spin tick', ro: 'Tick roată' },
                typicalDurationSeconds: LOOP_DURATION_SECONDS,
                description: {
                    en: 'Looped tick while the wheel spins.',
                    ro: 'Sunet în buclă cât timp roata se învârte.'
                },
                defaultPath: '/webgames/assets/wheel-of-fortune/wheel-spin-tick.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    },
    {
        gameCode: 'aviator-simplified',
        templateName: 'aviator-simplified.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: false,
        notes: {
            en: 'Aviator reads metadata.sfxCatalog from GET /config; engine loop stays template-local.',
            ro: 'Aviator citește metadata.sfxCatalog din GET /config; bucla de motor rămâne locală în template.'
        },
        moments: [
            {
                key: 'engineLoop',
                labels: { en: 'Engine loop', ro: 'Motor (buclă)' },
                typicalDurationSeconds: LOOP_DURATION_SECONDS,
                description: {
                    en: 'Looped engine ambience under flight (not metadata-driven).',
                    ro: 'Ambianță de motor în buclă în zbor (nu din metadata).'
                },
                defaultPath: '/webgames/assets/aviator/engine-loop.mp3',
                configurableInDashboard: false
            },
            {
                key: 'launch',
                labels: { en: 'Launch', ro: 'Lansare' },
                typicalDurationSeconds: 0.53,
                description: {
                    en: 'Take-off / launch feedback.',
                    ro: 'Feedback la decolare / lansare.'
                },
                defaultPath: '/webgames/sounds/flip_sound.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            {
                key: 'crashHit',
                labels: { en: 'Crash hit', ro: 'Impact crash' },
                typicalDurationSeconds: 1.32,
                description: {
                    en: 'Impact when the round crashes.',
                    ro: 'Impact când runda se prăbușește.'
                },
                defaultPath: '/webgames/sounds/reel_stop.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    },
    {
        gameCode: 'plinko',
        templateName: 'plinko.html',
        supportsMetadataSounds: true,
        supportsBackgroundMusic: true,
        notes: {
            en: 'Plinko reads metadata.backgroundMusic and metadata.sfxCatalog from GET /config.',
            ro: 'Plinko citește metadata.backgroundMusic și metadata.sfxCatalog din GET /config.'
        },
        moments: [
            {
                key: 'background',
                labels: { en: 'Background loop', ro: 'Fundal (buclă)' },
                typicalDurationSeconds: LOOP_DURATION_SECONDS,
                description: {
                    en: 'Optional looped ambience while the board is idle.',
                    ro: 'Ambianță opțională în buclă cât timp tabla e inactivă.'
                },
                binding: 'backgroundMusic',
                configurableInDashboard: true
            },
            {
                key: 'pegDing',
                labels: { en: 'Peg ding', ro: 'Sunet țintă' },
                typicalDurationSeconds: 0.2,
                description: {
                    en: 'Plays when the ball hits a peg.',
                    ro: 'La lovirea unei ținte de către bilă.'
                },
                defaultPath: '/webgames/assets/plinko/peg-ding.mp3',
                binding: 'sfxCatalog',
                configurableInDashboard: true
            },
            ...SHARED_WIN_LOSS_MOMENTS
        ]
    }
];

const profileByGameCode = new Map(WEB_GAME_SOUND_GAME_PROFILES.map(profile => [profile.gameCode, profile]));
const profileByTemplate = new Map(WEB_GAME_SOUND_GAME_PROFILES.map(profile => [profile.templateName, profile]));

export function webGameSoundProfileForGame(
    gameCode: string | null | undefined,
    htmlTemplateName?: string | null
): WebGameSoundGameProfile | null {
    const normalizedCode = gameCode?.trim();
    if (normalizedCode) {
        const byCode = profileByGameCode.get(normalizedCode);
        if (byCode) return byCode;
    }
    const template = htmlTemplateName?.trim();
    if (!template) return null;
    const templateKey = template.endsWith('.html') ? template : `${template}.html`;
    return profileByTemplate.get(templateKey) ?? null;
}

export function formatWebGameSoundMomentDuration(
    seconds: number | null,
    lang: WebGameSoundUiLang
): string {
    if (seconds == null) {
        return lang === 'ro' ? 'buclă' : 'loop';
    }
    if (seconds < 1) {
        const ms = Math.round(seconds * 1000);
        return lang === 'ro' ? `~${ms} ms` : `~${ms} ms`;
    }
    const rounded = Math.round(seconds * 10) / 10;
    return lang === 'ro' ? `~${rounded} s` : `~${rounded} s`;
}

export function mergeWebGameSfxCatalogFromEditor(
    existingCatalog: Record<string, unknown> | undefined,
    editorValues: Record<string, string>
): Record<string, string> {
    const merged: Record<string, string> = {};
    if (existingCatalog != null && typeof existingCatalog === 'object' && !Array.isArray(existingCatalog)) {
        Object.entries(existingCatalog).forEach(([key, value]) => {
            if (typeof value === 'string' && value.trim()) {
                merged[key] = value.trim();
            }
        });
    }
    Object.entries(editorValues).forEach(([key, value]) => {
        const trimmed = value.trim();
        if (trimmed) {
            merged[key] = trimmed;
        } else {
            delete merged[key];
        }
    });
    return merged;
}

export function webGameConfigurableSfxMomentKeys(): string[] {
    const keys = new Set<string>();
    WEB_GAME_SOUND_GAME_PROFILES.forEach(profile => {
        profile.moments.forEach(moment => {
            if (moment.configurableInDashboard && moment.binding === 'sfxCatalog') {
                keys.add(moment.key);
            }
        });
    });
    return Array.from(keys).sort();
}

export function webGameSoundSupportSummary(lang: WebGameSoundUiLang): string {
    const supported = WEB_GAME_SOUND_GAME_PROFILES
        .filter(profile => profile.supportsMetadataSounds)
        .map(profile => profile.gameCode)
        .join(', ');
    if (lang === 'ro') {
        return `Toate jocurile web folosesc metadata.backgroundMusic / metadata.sfxCatalog în runtime: ${supported}.`;
    }
    return `All web games use metadata.backgroundMusic / metadata.sfxCatalog at runtime: ${supported}.`;
}
